import { useState, useCallback, useRef, useEffect } from 'react';
import web3Provider from '../utils/web3Provider';
import { getBestRoute } from '../utils/routingV2';
import { ethers } from 'ethers';

export const useSwapQuoteV2 = () => {
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const debounceTimerRef = useRef(null);
  const lastFetchTimeRef = useRef(0);
  const MIN_FETCH_INTERVAL = 5000; // Minimum 5 seconds between quote fetches

  const fetchQuote = useCallback(async (fromToken, toToken, fromAmount, immediate = false) => {
    if (!fromToken || !toToken || !fromAmount || parseFloat(fromAmount) <= 0) {
      setQuote(null);
      setError(null);
      return;
    }

    // Clear any existing debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // If immediate flag is set, fetch right away (for token changes)
    // Otherwise wait 700ms - each keystroke resets the timer
    const debounceDelay = immediate ? 0 : 700;
    
    // Debounce the fetch - resets on each change, waits 700ms after last keystroke
    debounceTimerRef.current = setTimeout(async () => {
      // Check if enough time has passed since last fetch
      const now = Date.now();
      const timeSinceLastFetch = now - lastFetchTimeRef.current;
      
      if (timeSinceLastFetch < MIN_FETCH_INTERVAL) {
        // Schedule another attempt after the minimum interval
        const remainingTime = MIN_FETCH_INTERVAL - timeSinceLastFetch;
        debounceTimerRef.current = setTimeout(() => {
          fetchQuote(fromToken, toToken, fromAmount);
        }, remainingTime);
        return;
      }

      lastFetchTimeRef.current = now;
      setLoading(true);
      setError(null);

      try {
        console.log('🔍 Fetching V2 quote:', {
          from: fromToken.symbol,
          to: toToken.symbol,
          amount: fromAmount,
        });

        const provider = web3Provider.getProvider();
        if (!provider) {
          throw new Error('Provider not available');
        }

        // Get the best route across all DEXes
        const routeData = await getBestRoute(provider, fromToken, toToken, fromAmount);

        if (!routeData) {
          throw new Error('No route found');
        }

        console.log('✅ Best route found:', {
          dex: routeData.dex.name,
          type: routeData.type,
          amountOut: ethers.formatUnits(routeData.amountOut, toToken.decimals),
          feeAmount: ethers.formatUnits(routeData.feeAmount, toToken.decimals),
        });

        // Format the quote
        const formattedQuote = {
          estimatedOutput: routeData.amountOut.toString(),
          estimatedOutputFormatted: ethers.formatUnits(routeData.amountOut, toToken.decimals),
          feeAmount: routeData.feeAmount.toString(),
          feeAmountFormatted: ethers.formatUnits(routeData.feeAmount, toToken.decimals),
          amountOutBeforeFee: routeData.amountOutBeforeFee.toString(),
          dex: routeData.dex,
          dex1: routeData.dex1, // For cross-DEX swaps
          dex2: routeData.dex2, // For cross-DEX swaps
          crossDex: routeData.crossDex || false,
          path: routeData.path,
          fees: routeData.fees, // For V3 multi-hop
          fee: routeData.fee, // For V3 single-hop
          type: routeData.type,
          directPath: routeData.directPath,
          intermediateAmount: routeData.intermediateAmount?.toString(), // WFTM amount for cross-DEX
          allQuotes: routeData.allQuotes?.map(q => ({
            dex: q.dex.name,
            type: q.type,
            amountOut: ethers.formatUnits(q.amountOut, toToken.decimals),
          })) || [],
        };

        setQuote(formattedQuote);
        setError(null);
      } catch (err) {
        console.error('❌ Error fetching quote:', err);
        setError(err.message || 'Failed to fetch quote');
        setQuote(null);
      } finally {
        setLoading(false);
      }
    }, debounceDelay);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const resetQuote = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    setQuote(null);
    setError(null);
    setLoading(false);
  }, []);

  return {
    quote,
    loading,
    error,
    fetchQuote,
    resetQuote,
  };
};
